import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  Star,
  ChefHat,
  Sparkles,
  UserRound,
} from "lucide-react";
import { Marquee } from "@/components/Marquee";
import { Reveal } from "@/components/Reveal";
import { IMAGES, SERVICES, MANIFESTO, STATS } from "@/data/content";

const Eyebrow = ({ children }) => (
  <p className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.3em] text-bark">
    <span className="h-px w-10 bg-bark" aria-hidden="true" />
    {children}
  </p>
);

const Hero = () => (
  <section data-testid="hero-section" className="relative overflow-hidden pt-32 lg:pt-40">
    <div
      aria-hidden="true"
      className="absolute -right-40 -top-40 h-[480px] w-[480px] rounded-full bg-bark/5 blur-3xl"
    />
    <div className="relative mx-auto grid max-w-7xl items-center gap-16 px-4 pb-24 sm:px-6 lg:grid-cols-12 lg:px-8 lg:pb-32">
      <div className="lg:col-span-6">
        <Reveal y={14}>
          <Eyebrow>India's most trusted pet care brand</Eyebrow>
        </Reveal>
        <h1 className="mt-8 font-display text-5xl font-medium leading-[1.06] tracking-tight text-ink sm:text-6xl lg:text-7xl">
          <span className="mask-line">
            <span style={{ animationDelay: "0.15s" }}>Where every tail</span>
          </span>
          <span className="mask-line">
            <span style={{ animationDelay: "0.28s" }}>finds its</span>
          </span>
          <span className="mask-line">
            <span style={{ animationDelay: "0.41s" }}>
              <em className="font-editorial italic text-bark">happy rhythm.</em>
            </span>
          </span>
        </h1>
        <Reveal delay={0.55}>
          <p className="mt-7 max-w-lg text-base leading-relaxed text-slate-600 sm:text-lg">
            Boarding, day care, grooming, training, events and vaccinations — one facility designed
            around pups, and the people who love them.
          </p>
        </Reveal>
        <Reveal delay={0.68}>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Link
              to="/services"
              data-testid="hero-cta-services"
              className="group inline-flex items-center gap-2 rounded-full bg-navy px-7 py-4 text-sm font-semibold text-cream transition-colors duration-200 hover:bg-ink"
            >
              Explore our services
              <ArrowRight
                className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1"
                aria-hidden="true"
              />
            </Link>
            <Link
              to="/signin"
              data-testid="hero-cta-login"
              className="inline-flex items-center gap-2 rounded-full border border-navy/15 px-7 py-4 text-sm font-semibold text-navy transition-colors duration-200 hover:border-bark hover:text-bark"
            >
              <UserRound className="h-4 w-4" aria-hidden="true" />
              Login &amp; check plans
            </Link>
          </div>
        </Reveal>
        <Reveal delay={0.8}>
          <dl className="mt-14 flex flex-wrap gap-x-12 gap-y-6 border-t border-ink/10 pt-8">
            {STATS.map((s) => (
              <div key={s.label}>
                <dt className="sr-only">{s.label}</dt>
                <dd className="font-display text-3xl font-medium text-navy">{s.value}</dd>
                <dd className="mt-1 text-xs font-medium uppercase tracking-wider text-slate-500">
                  {s.label}
                </dd>
              </div>
            ))}
          </dl>
        </Reveal>
      </div>

      <div className="lg:col-span-6">
        <Reveal delay={0.3}>
          <div className="relative mx-auto max-w-md lg:ml-auto">
            <img
              src={IMAGES.hero}
              alt="A happy dog enjoying day care"
              data-testid="hero-image-frame"
              className="aspect-[4/5] w-full rounded-t-[12rem] rounded-b-[2rem] object-cover ring-1 ring-ink/10"
            />
            <div
              data-testid="hero-rating-badge"
              className="absolute bottom-8 -left-4 flex items-center gap-2 rounded-full bg-white/90 px-5 py-3 shadow-lg backdrop-blur-md sm:-left-10"
            >
              <Star className="h-4 w-4 fill-amberwarm text-amberwarm" aria-hidden="true" />
              <span className="text-sm font-semibold text-ink">4.9 on Google</span>
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

const ServicesTeaser = () => (
  <section
    data-testid="services-grid"
    className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8 lg:py-32"
  >
    <div className="flex flex-wrap items-end justify-between gap-6">
      <Reveal>
        <Eyebrow>What we do</Eyebrow>
        <h2 className="mt-5 max-w-xl font-display text-3xl font-medium tracking-tight text-ink sm:text-4xl lg:text-5xl">
          Six ways we care for your pup,{" "}
          <em className="font-editorial italic text-bark">beautifully</em>
        </h2>
      </Reveal>
      <Reveal delay={0.15}>
        <Link
          to="/services"
          data-testid="services-view-all-link"
          className="group inline-flex items-center gap-2 text-sm font-semibold text-navy"
        >
          View all services
          <ArrowUpRight
            className="h-4 w-4 text-bark transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
            aria-hidden="true"
          />
        </Link>
      </Reveal>
    </div>

    <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
      {SERVICES.map((s, i) => {
        const Icon = s.icon;
        return (
          <Reveal key={s.name} delay={i * 0.06}>
            <Link
              to="/services"
              data-testid={`service-card-${s.name.toLowerCase().replace(/\s+/g, "-")}`}
              className="group flex h-full flex-col rounded-3xl border border-ink/8 bg-white p-8 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[0_28px_50px_-28px_rgba(15,23,42,0.3)]"
            >
              <span className="w-fit rounded-full bg-bark-soft p-3 text-bark">
                <Icon className="h-5 w-5" aria-hidden="true" />
              </span>
              <h3 className="mt-6 font-display text-2xl font-medium tracking-tight text-ink">
                {s.name}
              </h3>
              <p className="mt-1 text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                {s.tag}
              </p>
              <p className="mt-4 flex-1 text-sm leading-relaxed text-slate-600">{s.blurb}</p>
              <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-navy">
                Learn more
                <ArrowRight
                  className="h-4 w-4 text-bark transition-transform duration-200 group-hover:translate-x-1"
                  aria-hidden="true"
                />
              </span>
            </Link>
          </Reveal>
        );
      })}
    </div>
  </section>
);

const Manifesto = () => (
  <section data-testid="facility-manifesto" className="bg-cream-dark/50 py-24 lg:py-32">
    <div className="mx-auto grid max-w-7xl gap-16 px-4 sm:px-6 lg:grid-cols-12 lg:px-8">
      <div className="lg:col-span-4">
        <div className="lg:sticky lg:top-28">
          <Reveal>
            <Eyebrow>The standard</Eyebrow>
            <h2 className="mt-5 font-display text-3xl font-medium tracking-tight text-ink sm:text-4xl">
              Built while keeping pups — and their needs —{" "}
              <em className="font-editorial italic text-bark">in mind</em>
            </h2>
            <p className="mt-5 text-base leading-relaxed text-slate-600">
              Five decisions we made before we opened our doors, and still make every single day.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <img
              src={IMAGES.pomeranian}
              alt="A relaxed pup at the facility"
              className="mt-12 hidden w-full max-w-xs rounded-[2rem] object-cover ring-1 ring-ink/10 lg:block"
            />
          </Reveal>
        </div>
      </div>
      <div className="lg:col-span-8">
        <ol>
          {MANIFESTO.map((ch, i) => (
            <Reveal key={ch.num} delay={i * 0.05}>
              <li
                data-testid={`manifesto-chapter-${ch.num}`}
                className={`grid gap-3 py-9 sm:grid-cols-[auto_1fr] sm:gap-10 ${
                  i > 0 ? "border-t border-ink/8" : "pt-0"
                }`}
              >
                <span className="font-display text-lg font-medium text-bark">{ch.num}</span>
                <div>
                  <h3 className="font-display text-2xl font-medium tracking-tight text-ink">
                    {ch.title}
                  </h3>
                  <p className="mt-3 max-w-2xl text-base leading-relaxed text-slate-600">
                    {ch.text}
                  </p>
                </div>
              </li>
            </Reveal>
          ))}
        </ol>
      </div>
    </div>
  </section>
);

const Meals = () => (
  <section
    data-testid="fresh-meals-section"
    className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8 lg:py-32"
  >
    <div className="grid items-center gap-16 lg:grid-cols-2">
      <div>
        <Reveal>
          <Eyebrow>Meal care</Eyebrow>
          <h2 className="mt-5 font-display text-3xl font-medium tracking-tight text-ink sm:text-4xl lg:text-5xl">
            Freshly cooked meals,{" "}
            <em className="font-editorial italic text-bark">like home</em>
          </h2>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mt-6 max-w-lg text-base leading-relaxed text-slate-600 sm:text-lg">
            Every guest gets two or three freshly cooked, meat-based meals a day — portioned by age,
            with absolutely no preservatives.
          </p>
        </Reveal>
        <Reveal delay={0.25}>
          <ul className="mt-8 space-y-5">
            {[
              { icon: ChefHat, text: "Cooked fresh daily — no preservatives, ever" },
              { icon: Sparkles, text: "Treats and snacks woven into training and enrichment" },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <li key={item.text} className="flex items-center gap-4">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-bark-soft text-bark">
                    <Icon className="h-4 w-4" aria-hidden="true" />
                  </span>
                  <span className="text-base text-slate-700">{item.text}</span>
                </li>
              );
            })}
          </ul>
          <p className="mt-8 border-l-2 border-bark pl-5 font-display text-lg italic text-navy/80">
            Supervised play and socialisation between every meal and nap.
          </p>
        </Reveal>
      </div>
      <Reveal delay={0.2}>
        <img
          src={IMAGES.meal}
          alt="Freshly cooked, preservative-free meal being prepared for pups"
          className="aspect-[4/3] w-full rounded-[2rem] object-cover ring-1 ring-ink/10"
        />
      </Reveal>
    </div>
  </section>
);

const LoginNudge = () => (
  <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-6 lg:px-8 lg:pb-32">
    <Reveal>
      <div
        data-testid="login-cta-card"
        className="relative overflow-hidden rounded-[2rem] bg-navy px-7 py-16 sm:px-12 lg:px-20"
      >
        <div
          aria-hidden="true"
          className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-bark/15 blur-3xl"
        />
        <div className="relative grid items-center gap-10 lg:grid-cols-[1.4fr_1fr]">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-amberwarm">
              Customer portal — coming soon
            </p>
            <h2 className="mt-5 font-display text-3xl font-medium tracking-tight text-cream sm:text-4xl">
              Your pup's whole world,{" "}
              <em className="font-editorial italic text-amberwarm">one login away</em>
            </h2>
            <p className="mt-4 max-w-xl text-base leading-relaxed text-cream/60">
              Soon you'll be able to create your pup's profile, check their records and book
              boarding, day care, grooming and more — all from one place.
            </p>
          </div>
          <div className="flex flex-col items-start gap-4 lg:items-end">
            <Link
              to="/signin"
              data-testid="login-nudge-button"
              className="group inline-flex items-center gap-2 rounded-full bg-bark px-8 py-4 text-sm font-semibold text-white transition-colors duration-200 hover:bg-bark-dark"
            >
              <UserRound className="h-4 w-4" aria-hidden="true" />
              Sign in to your account
              <ArrowRight
                className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1"
                aria-hidden="true"
              />
            </Link>
            <p className="text-xs text-cream/40">
              New here? You'll be able to register your pup soon.
            </p>
          </div>
        </div>
      </div>
    </Reveal>
  </section>
);

export default function Home() {
  return (
    <>
      <Hero />
      <Marquee />
      <ServicesTeaser />
      <Manifesto />
      <Meals />
      <LoginNudge />
    </>
  );
}
